import React from 'react';
import CardCommon from '@op/components/CardCommon';
import RenderGameIcon from '@op/components/RenderImg/renderGameIcon';
import useIntls from '@opm/hooks/useIntls';
import KPICell from '@opm/components/KPICell';
import type { TGameMap } from '@opm/pages/CompetitorAdComparison/hooks/useGameMap';
import { ITrendDetail } from '../../../types';
import { TTopicTypeColorMap } from '../../../utils';
import TrendTopicTags from '../../../components/TrendTopicTags';
import { useGameMeta } from '../../../components/useGameMeta';
import AISummaryBlock from '../../../components/AISummaryBlock';
import './index.scss';
import { DBO_V } from '@op/utils/format';

interface ITrendInfoProps {
  data: ITrendDetail | null;
  loading: boolean;
  /** 当前 filter（用于 DoD 文案动态切换 = DoD/WoW/MoM） */
  filter?: any;
  /** 游戏元信息字典（cond.materials_config_games）：id → meta */
  gameMap?: TGameMap;
  /** Topic Type chip 颜色映射（按 L1 parent 取色，L2 共享 parent 色） */
  topicTypeColorMap?: TTopicTypeColorMap;
}

/**
 * 详情页 TrendInfo 信息卡（按 Figma 3902_51518 设计）
 *
 * 布局（自上而下）：
 *  ┌────────────────────────────────────────────────────────────────┐
 *  │ Game      Impressions   Creatives  Avg.Days  Type & Elements    │
 *  │ 🎮Name    999.99M       999.99M    65        [L1 绿] [L2 黄]    │
 *  │           DoD ↑88%      DoD ↑88%                                │
 *  ├────────────────────────────────────────────────────────────────┤
 *  │ ✨ AI Summary                                                   │
 *  │ <蓝色渐变面板> AI 摘要文本                                       │
 *  └────────────────────────────────────────────────────────────────┘
 *
 * Type & Elements chip 颜色约定（与 CardView 保持一致）：
 *   L1 → 绿色 #E8F1D9 / #72A611
 *   L2 → 黄色 #FBF3DD / #E9B32B
 */
export default function TrendInfo({
  data,
  loading,
  filter,
  gameMap,
  topicTypeColorMap,
}: ITrendInfoProps) {
  const { intl } = useIntls();

  const { gameName, gameLogo: gameCover } = useGameMeta(data, gameMap);
  return (
    <CardCommon
      className="dbom-ct-trend-info"
      loading={loading}
      loadingHeight={302}
      emptyHeight={302}
      title={intl('Basics')}
      empty={!data || Object.keys(data).length === 0}
      hasEmptyImg={false}
      contentCard={false}
    >
      {/* KPI 行：Game / Impressions / Creatives / Avg.Days / Type & Elements */}
      <div className="dbom-ct-trend-info__kpi-row">
        {/* Game：使用 KPICell 的"无 DoD"模式（label + 自定义内容代替数值） */}
        <div className="dbom-ct-trend-info__col">
          <div className="dbom-ct-trend-info__label">{intl('Game')}</div>
          <div className="dbom-ct-trend-info__game">
            <RenderGameIcon
              className="dbom-ct-trend-info__game-logo"
              gameInfo={{
                cover: gameCover,
                entity_type: (data as any)?.entity_type,
                edition_unified_id: data?.game_id,
              }}
            />
            <span className="dbom-ct-trend-info__game-name" title={gameName}>
              {gameName}
            </span>
          </div>
        </div>

        {/* Impressions */}
        <KPICell
          label={intl('Impressions')}
          kpiData={data?.cumulative_impressions}
          filter={filter}
          size="lg"
        />

        {/* Creatives */}
        <KPICell
          label={intl('Ads-1')}
          kpiData={data?.creatives}
          filter={filter}
          size="lg"
        />

        {/* Avg. Days Active（按 list.md 文档：不做环比） */}
        <KPICell
          label={intl('Avg. Days Active')}
          value={data?.avg_days_active}
          formatValue={(value) => DBO_V.reviews(value, 1)}
          size="lg"
        />

        {/* Type & Elements */}
        <div className="dbom-ct-trend-info__col dbom-ct-trend-info__col--tags">
          <div className="dbom-ct-trend-info__label">
            {intl('Type & Elements')}
          </div>
          <div className="dbom-ct-trend-info__tags">
            <TrendTopicTags
              record={data}
              topicTypeColorMap={topicTypeColorMap}
              chipClassName="dbom-ct-trend-info__chip"
            />
          </div>
        </div>
      </div>

      {/* AI Summary */}
      <AISummaryBlock summaryData={data} hasBorder />
    </CardCommon>
  );
}
